import { useEffect } from 'react'
import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { useLazyCheckoutSessionRule } from '../../graphQL/queries/checkoutSessionRule'
import type { ICheckoutSessionRuleResponse } from '../../graphQL/queries/checkoutSessionRule/types'
import {
  markRecoveryRedirect,
  selectRecoveryRedirects,
  setLastAppliedRules,
} from '../../redux/slices/checkout'
import { generateDataDogContext } from '../../utils/dataDog'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { useCheckoutRuleRedirect } from './useCheckoutRuleRecovery'

const FUNCTION_NAME = 'useCheckoutRulePreflight'

/**
 * Asks the rule engine what still blocks this cart, and routes out of it.
 *
 * Mounted by `templates/Cart.tsx`, which is the earliest screen where the
 * answer is actionable and the last one before the shopper commits. It asks
 * `checkoutSessionRule` with **no** `ruleType`, so the response carries every
 * rule rather than one, and lands the whole list in `checkoutSlice` for
 * `useCheckoutRuleRecovery` and COM-1045's cart rows to read.
 *
 * ⚠️ **Inert on the fleet today.** The engine fails open until COM-736 step 2,
 * so `resultCheck` never goes false and no rule reports `blocks && !checked`;
 * and no storefront authors `checkoutRuleRoutes` until COM-951, so even a
 * blocking rule would find nowhere to send the shopper. Tests are the
 * evidence that this works, and a green suite is not evidence that either
 * landed.
 *
 * Mount it once. Two instances would issue two queries and race each other's
 * write to `lastAppliedRules` — the same caution `useCheckoutRuleOptions`
 * carries.
 */
export const useCheckoutRulePreflight = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const redirectToUnmetRule = useCheckoutRuleRedirect()
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const storeName = useAppSelector((state) => state.defaultSlice.storeName)

  /**
   * Stores every rule the engine named, then routes out of the first that
   * blocks and has a screen behind it.
   *
   * `rules ?? []` rather than a bail-out: the field is nullable on the schema
   * and an answer naming no rules is the ordinary one while the engine fails
   * open. Writing `[]` is what separates "asked, nothing blocks" from the
   * `undefined` that means no pre-flight has run — a distinction COM-1045's
   * CTA needs and `resetSelectionsForNewSession` preserves.
   */
  const onCompleted = (response: ICheckoutSessionRuleResponse) => {
    const rules = response.checkoutSessionRule?.rules ?? []
    dispatch(setLastAppliedRules(rules))

    /*
     * The loop guard, read fresh from the store rather than from a render
     * snapshot.
     *
     * This callback runs a round trip after its closure was built, and the
     * value it needs — which rule types this session has already been sent to
     * — is written by the very redirect it is about to perform. A
     * `useAppSelector` snapshot would be as old as the request. COM-974's
     * rule, and the same reason `useCheckoutRuleSelection` reads its rollback
     * state this way.
     *
     * ⚠️ **Without this filter the shopper cannot reach their cart.** Entry
     * redirects to the picker, the picker's `back` is `useDynamicBack` —
     * `navigate(-1)` — which lands on the cart, which redirects again.
     * COM-1046's *Continuar* CTA accepts that loop deliberately because it
     * needs a tap each time round — it navigates from the handler behind the
     * button and never from an effect, so a shopper who stops tapping stops
     * moving; this one needs nothing. The second entry
     * therefore leaves the shopper on the cart, with
     * `hasUnmetBlockingRules` still `true` for that CTA to act on.
     */
    const alreadySent = selectRecoveryRedirects(store.getState())
    const unmet = rules
      .filter((rule) => rule.blocks && !rule.checked)
      .map((rule) => rule.ruleType)
      .filter((ruleType) => !alreadySent.includes(ruleType))

    const sentTo = redirectToUnmetRule({ types: unmet })
    if (sentTo) dispatch(markRecoveryRedirect(sentTo))
  }

  /**
   * Reports and stops there.
   *
   * **No toast and no `manageErrors`**, deliberately departing from
   * `useGetCart`'s three-part failure shape. Nothing on the cart waits on this
   * answer: the shopper asked to see their cart and their cart is intact, so a
   * background read that failed is not their problem to act on. An error
   * screen over a working cart would be strictly worse than the silence.
   */
  const onError = (error: ApolloError) => {
    const context = generateDataDogContext({
      title: 'checkoutSessionRule pre-flight threw on cart entry',
      extraInfo: { function: FUNCTION_NAME },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const [fetchRules] = useLazyCheckoutSessionRule({ onCompleted, onError })

  /**
   * Ask on mount, and again if the session or the storefront moves beneath a
   * mounted cart.
   *
   * `sessionId` is a guard as well as a dependency, for the reason
   * `useCheckoutRuleOptions` sets out: `Config` dispatches it from an effect
   * and effects run children-first, so on the commit that first renders this
   * screen it is still `undefined` here. `checkoutSessionRule` declares
   * `$sessionUid: ID!` and Apollo drops an `undefined` variable from the
   * request body. `''` is a session that exists and is unusable, so it is
   * passed through and the server's rejection reaches `onError`.
   *
   * ⚠️ **Dispatch no loader here.** `routes/index.tsx` computes
   * `isBooting = loaders.some((name) => name !== LoadersType.CREATE_ORDER)`,
   * and while that is set `<Routes>` registers nothing but the `path="*"`
   * catch-all — so raising one would unmount the cart this hook is mounted on
   * and delete the screen mid-read. COM-858's failure class.
   * `useValidateLinkedProducts` brackets its own call to this query with
   * `setSynchronizingCart` for the same reason; this needs neither, because
   * nothing on the screen waits for it.
   *
   * ⚠️ **No ref, and `StrictMode` is not a reason to add one.** React 18
   * double-invokes mount effects only on a concurrent root, and `index.tsx`
   * still mounts through the legacy `ReactDOM.render`. The suite is the
   * stricter environment — `@testing-library/react` renders through
   * `createRoot` — so a test wrapping this in `<StrictMode>` sees two fetches
   * where the app sees one. The `recoveryRedirects` latch is what makes a
   * second fetch harmless anyway.
   */
  useEffect(() => {
    if (sessionId === undefined) return

    fetchRules({
      variables: {
        storefrontName: storeName || '',
        sessionUid: sessionId,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, storeName])
}

export default useCheckoutRulePreflight
